
import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import FilamentManagementSidebar from '@/components/admin/FilamentManagementSidebar';
import FilamentManagementContent from '@/components/admin/FilamentManagementContent';

const FilamentManagement = () => {
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<'filaments' | 'manufacturers' | 'printers' | 'tutorials' | 'page-access'>('filaments');

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'filaments':
        return 'Filament Management';
      case 'manufacturers':
        return 'Manufacturer Management';
      case 'printers':
        return 'Printer Management';
      case 'tutorials':
        return 'Tutorial Management';
      case 'page-access':
        return 'Page Access Control';
      default:
        return 'Management';
    }
  };

  const getSectionDescription = () => {
    switch (activeSection) {
      case 'filaments':
        return 'Manage filaments, manufacturers, and printer models with full admin control';
      case 'manufacturers':
        return 'Manage manufacturers and their information';
      case 'printers':
        return 'Manage printer models and specifications';
      case 'tutorials':
        return 'Manage tutorials, approve submissions, and moderate content';
      case 'page-access':
        return 'Control which sections are accessible to non-admin users';
      default:
        return 'Select a section to manage';
    }
  };

  // Create sidebar component
  const sidebar = (
    <FilamentManagementSidebar
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedManufacturer={selectedManufacturer}
      setSelectedManufacturer={setSelectedManufacturer}
      selectedType={selectedType}
      setSelectedType={setSelectedType}
      selectedStatus={selectedStatus}
      setSelectedStatus={setSelectedStatus}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
    />
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header 
        useBackButton={false}
        pageName="Management"
        sidebarExpanded={sidebarExpanded}
        toggleSidebar={toggleSidebar}
      />
      
      <div className="flex flex-1 pt-16">
        {/* Sidebar */}
        <div className={`transition-all duration-300 ease-in-out ${sidebarExpanded ? 'w-64' : 'w-0'} overflow-hidden`}>
          <div className="h-full w-64">
            {sidebar}
          </div>
        </div>
        
        {/* Main content area */}
        <div className="flex-1 overflow-hidden">
          <div className="container py-8 space-y-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
                <ArrowLeft size={16} className="mr-2" />
                Back to Admin Dashboard
              </Button>
              <div>
                <h1 className="text-3xl font-bold">{getSectionTitle()}</h1>
                <p className="text-muted-foreground">
                  {getSectionDescription()}
                </p>
              </div>
            </div>

            <FilamentManagementContent
              searchQuery={searchQuery}
              selectedManufacturer={selectedManufacturer}
              selectedType={selectedType}
              selectedStatus={selectedStatus}
              activeSection={activeSection}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilamentManagement;
